import { Graphics, type Sprite } from 'pixi.js';
import type { ChargedAttack } from '../../data/full/echassier';
import { clamp01, lerp, smoothstep } from '../../shared/scalar';
import type { PlayerState, ProjectileState, SimState } from '../../sim/state';
import { statValue } from '../../sim/stats';
import type { Family, RenderContext } from '../context';
import type { Frame } from '../frame';
import { TAU } from '../paint';
import { STREAK_HEAD } from '../streak';
import { REFERENCE_RADIUS } from '../textures-looks';
import { add, byId, hide, lookup, placeOutline, setTint } from '../util';
import { ViewPool } from '../views';
import { HAT_HEIGHT, lightAngle, shadowReach } from './stilt-shadow';

// Out of his hat, it rises a little, then comes down onto its own shadow and skims the ground.
const DIVE_TICKS = 4;
const RISE = 0.4;
const NEAR = 0.35;
const SHADOW = { far: 0.35, near: 0.75, ground: 0.55, flat: 0.45 } as const;
// A full charge flies 2.5 times bigger than an empty one, white-hot, a trail twice as long, more glow.
const SIZE = { empty: 0.6, full: 1.5, trail: 1 } as const;
const CORE = { from: 0.55, to: 0.72 } as const;
const GLOW = { from: 2.5, to: 4, alphaFrom: 0.3, alphaTo: 0.6 } as const;
// A bead of light circles it for each bad vibe it will still cross; one flies off at each crossed.
const BEAD = { gap: 4, spread: 2.5, size: 2, grow: 1, turnsPerBeat: 1.1 } as const;
const FLY = { beats: 0.35, reach: 20 } as const;
const BEADS = 5;
const HAT_RING = {
  beats: [0.25, 0.4],
  from: 9,
  to: [33, 60],
  width: [2, 3.4],
  alpha: 0.7,
} as const;
const SECOND_RING = { delay: 0.09, size: 0.75 } as const;
const TOUCH = { beats: 0.45, from: 4, to: [16, 30], width: [1.4, 2.6], white: 0.5 } as const;

interface ShotView {
  readonly glow: Sprite;
  readonly trail: Sprite;
  readonly outline: Sprite;
}

interface Flight {
  readonly share: number;
  readonly born: number;
  pierceLeft: number;
  landed: boolean;
}

interface Ring {
  readonly x: number;
  readonly y: number;
  readonly at: number;
  readonly beats: number;
  readonly from: number;
  readonly to: number;
  readonly width: number;
  readonly color: number;
}

interface Bead {
  readonly x: number;
  readonly y: number;
  readonly angle: number;
  readonly orbit: number;
  readonly size: number;
  readonly at: number;
  readonly color: number;
}

// How high above the ground, in the drawing units of his shadow, `share` of the way down.
function heightAt(share: number): number {
  return HAT_HEIGHT * (1 - share + RISE * Math.sin(Math.PI * share));
}

// From 0, an empty shot, to 1, a full one: read from its damage against his own.
function chargeOf(shot: ProjectileState, player: PlayerState, attack: ChargedAttack): number {
  const empty = statValue(player, 'damageMul', attack.damage);
  const full = statValue(player, 'damageMul', attack.fullDamage);
  return full > empty ? clamp01((shot.damage - empty) / (full - empty)) : 0;
}

export function createPlunge(ctx: RenderContext): Family {
  const { layers, textures: t } = ctx;
  const attacks = new Map<string, ChargedAttack>();
  for (const [classId, attack] of ctx.attacks) {
    if (attack.kind === 'charged') {
      attacks.set(classId, attack);
    }
  }
  const shadows = new Graphics();
  layers.dim.addChild(shadows);
  const rings = new Graphics();
  layers.fx.addChild(rings);
  const lights = new Graphics();
  layers.weapons.addChild(lights);
  const shots = new ViewPool<ShotView>(
    () => ({
      glow: add(layers.glow, t.halo),
      outline: add(layers.shots, t.streak, STREAK_HEAD),
      trail: add(layers.shots, t.streak, STREAK_HEAD),
    }),
    (view) => {
      hide(view.glow, view.trail, view.outline);
    },
  );
  const flights = new Map<number, Flight>();
  const firedAt = new Map<number, number>();
  const live: Ring[] = [];
  const flying: Bead[] = [];

  function drawRing(ring: Ring, frame: Frame): boolean {
    const life = (frame.now - ring.at) / (ring.beats * frame.tempo.ticksPerBeat);
    if (life >= 1) {
      return false;
    }
    if (life >= 0) {
      const strength = (1 - life) * (frame.calm ? 0.55 : 1);
      rings
        .circle(ring.x, ring.y, ring.from + (ring.to - ring.from) * life)
        .stroke({ width: ring.width, color: ring.color, alpha: 0.7 * strength });
    }
    return true;
  }

  function drawBead(bead: Bead, frame: Frame): boolean {
    const life = (frame.now - bead.at) / (FLY.beats * frame.tempo.ticksPerBeat);
    if (life >= 1) {
      return false;
    }
    const reach = bead.orbit + FLY.reach * smoothstep(life);
    const x = bead.x + Math.cos(bead.angle) * reach;
    const y = bead.y + Math.sin(bead.angle) * reach;
    lights.circle(x, y, bead.size * (1 - 0.5 * life)).fill({ color: 0xffffff, alpha: 1 - life });
    lights
      .circle(x, y, bead.size * 1.8 * (1 - 0.5 * life))
      .stroke({ width: 1, color: bead.color, alpha: 1 - life });
    return true;
  }

  // On release, a ring opens at his hat, wider with the charge, and a second one at full charge.
  function release(
    x: number,
    y: number,
    flight: Flight,
    color: number,
    scale: number,
    frame: Frame,
  ): void {
    const { share } = flight;
    const beats = lerp(HAT_RING.beats[0], HAT_RING.beats[1], share);
    const to = lerp(HAT_RING.to[0], HAT_RING.to[1], share) * scale;
    const width = lerp(HAT_RING.width[0], HAT_RING.width[1], share) * scale;
    const from = HAT_RING.from * scale;
    live.push({ x, y, at: flight.born, beats, from, to, width, color });
    if (share >= 1) {
      const at = flight.born + SECOND_RING.delay * frame.tempo.ticksPerBeat;
      live.push({ x, y, at, beats, from, to: to * SECOND_RING.size, width, color });
    }
  }

  function land(x: number, y: number, flight: Flight, color: number, scale: number, now: number) {
    const { share } = flight;
    const to = lerp(TOUCH.to[0], TOUCH.to[1], share) * scale;
    const width = lerp(TOUCH.width[0], TOUCH.width[1], share) * scale;
    const from = TOUCH.from * scale;
    live.push({ x, y, at: now, beats: TOUCH.beats, from, to, width, color });
    if (share > TOUCH.white) {
      live.push({
        x,
        y,
        at: now,
        beats: TOUCH.beats,
        from,
        to: to * 0.6,
        width: scale,
        color: 0xffffff,
      });
    }
  }

  return {
    reset(): void {
      shots.releaseAll();
      flights.clear();
      firedAt.clear();
      live.length = 0;
      flying.length = 0;
      shadows.clear();
      rings.clear();
      lights.clear();
    },
    update(state: SimState, alpha: number, frame: Frame): void {
      const { palette, light, now, calm, tempo } = frame;
      shadows.clear();
      rings.clear();
      lights.clear();
      const angle = lightAngle(frame.fraction);
      const lightX = Math.cos(angle);
      const lightY = Math.sin(angle);
      shots.begin();
      for (const shot of state.projectiles) {
        const { owner } = shot;
        if (owner.kind !== 'player') {
          continue;
        }
        const player = byId(state.players, owner.playerId);
        const attack = player === undefined ? undefined : attacks.get(player.classId);
        if (player === undefined || attack === undefined) {
          continue;
        }
        const x = lerp(shot.prevX, shot.x, alpha);
        const y = lerp(shot.prevY, shot.y, alpha);
        const scale = player.radius / REFERENCE_RADIUS;
        const color = palette[lookup(ctx.classTokens, player.classId, 'class')];
        let flight = flights.get(shot.id);
        if (flight === undefined) {
          flight = {
            share: chargeOf(shot, player, attack),
            born: now,
            pierceLeft: shot.pierceLeft,
            landed: false,
          };
          flights.set(shot.id, flight);
          if (firedAt.get(player.id) !== state.tick) {
            firedAt.set(player.id, state.tick);
            const px = lerp(player.prevX, player.x, alpha);
            const py = lerp(player.prevY, player.y, alpha);
            release(px, py, flight, color, scale, frame);
          }
        }
        const { share } = flight;
        const dive = clamp01((attack.rangeTicks - shot.ticksLeft - 1 + alpha) / DIVE_TICKS);
        const height = dive < 1 ? heightAt(dive) : 0;
        const near = 1 - height / (HAT_HEIGHT * (1 + RISE));
        const radius = shot.radius * lerp(SIZE.empty, SIZE.full, share);
        if (dive < 1) {
          const reach = shadowReach(scale, height);
          shadows
            .ellipse(
              x + lightX * reach,
              y + lightY * reach,
              radius * (0.8 + 0.5 * near),
              radius * (0.4 + 0.25 * near),
            )
            .fill({ color: 0, alpha: SHADOW.far + (SHADOW.near - SHADOW.far) * near });
        } else {
          if (!flight.landed) {
            flight.landed = true;
            land(x, y, flight, color, scale, now);
          }
          shadows
            .ellipse(x + 1, y + 1.5, radius * 1.1, radius * SHADOW.flat)
            .fill({ color: 0, alpha: SHADOW.ground });
        }
        const lift = 1 + (NEAR * height) / HAT_HEIGHT;
        const size = (radius / t.streak.radius) * lift;
        const heading = Math.atan2(shot.vy, shot.vx);
        const { glow, trail, outline } = shots.acquire(shot.id);
        setTint(trail, color);
        trail.visible = true;
        trail.alpha = light.haloAlpha;
        trail.position.set(x, y);
        trail.rotation = heading;
        trail.scale.set(size * (1 + SIZE.trail * share), size * 0.6);
        placeOutline(outline, trail, t.streak.texture, t.streak.radius, frame);
        glow.visible = true;
        setTint(glow, color);
        glow.position.set(x, y);
        glow.scale.set((shot.radius * lift * lerp(GLOW.from, GLOW.to, share)) / t.halo.radius);
        glow.alpha = lerp(GLOW.alphaFrom, GLOW.alphaTo, share) * light.haloAlpha;
        lights
          .circle(x, y, radius * lift * lerp(CORE.from, CORE.to, share))
          .fill({ color: 0xffffff, alpha: share });

        const beads = Math.min(BEADS, shot.pierceLeft + 1);
        const orbit = (radius + (BEAD.gap + BEAD.spread * share) * scale) * lift;
        const bead = (BEAD.size + BEAD.grow * share) * scale;
        const spin = calm
          ? 0
          : ((now - flight.born) / tempo.ticksPerBeat) * BEAD.turnsPerBeat * TAU;
        const turnOf = (index: number, count: number) => spin + (index * TAU) / count;
        for (let crossed = shot.pierceLeft; crossed < flight.pierceLeft; crossed += 1) {
          const count = Math.min(BEADS, crossed + 2);
          flying.push({
            x,
            y,
            angle: turnOf(count - 1, count),
            orbit,
            size: bead,
            at: now,
            color,
          });
        }
        flight.pierceLeft = shot.pierceLeft;
        for (let index = 0; index < beads; index += 1) {
          const turn = turnOf(index, beads);
          const bx = x + Math.cos(turn) * orbit;
          const by = y + Math.sin(turn) * orbit;
          lights.circle(bx, by, bead * 1.8).fill({ color, alpha: 0.6 });
          lights.circle(bx, by, bead).fill({ color: 0xffffff });
        }
      }
      shots.end();
      for (const id of flights.keys()) {
        if (byId(state.projectiles, id) === undefined) {
          flights.delete(id);
        }
      }
      let kept = 0;
      for (const ring of live) {
        if (drawRing(ring, frame)) {
          live[kept] = ring;
          kept += 1;
        }
      }
      live.length = kept;
      kept = 0;
      for (const bead of flying) {
        if (drawBead(bead, frame)) {
          flying[kept] = bead;
          kept += 1;
        }
      }
      flying.length = kept;
    },
    destroy(): void {
      shadows.destroy();
      rings.destroy();
      lights.destroy();
    },
  };
}
